import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  Button,
  Typography,
  Descriptions,
  Space,
  message,
  Row,
  Col,
  Tag,
  Avatar,
  Empty,
  Modal,
} from 'antd';
import {
  ArrowLeftOutlined,
  EditOutlined,
  PhoneOutlined,
  MailOutlined,
  UserOutlined,
  CarOutlined,
  PlusOutlined,
  TrophyOutlined,
  MessageOutlined,
} from '@ant-design/icons';
import { Customer, Vehicle } from '../types';
import { customerApi, vehicleApi } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import VehicleCard from './VehicleCard';
import VehicleForm from './VehicleForm';
import CustomerRewardsTab from './admin/CustomerRewardsTab';
import CustomerCommunicationTab from './admin/CustomerCommunicationTab';

const { Title } = Typography;

// Helper function to get role-based path prefix
const getRolePrefix = (userRole?: string): string => {
  return userRole === 'customer' ? '/portal' :
         userRole === 'technician' ? '/technician' :
         userRole === 'advisor' ? '/advisor' :
         userRole === 'owner' ? '/owner' : '/admin';
};

const CustomerDetail: React.FC = () => {
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [loading, setLoading] = useState(true);
  const [, setVehiclesLoading] = useState(true);
  const [addVehicleModalVisible, setAddVehicleModalVisible] = useState(false);
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const rolePrefix = getRolePrefix(user?.role);

  useEffect(() => {
    if (id) {
      fetchCustomer(parseInt(id));
      fetchVehicles(parseInt(id));
    }
  }, [id]);

  const fetchCustomer = async (customerId: number) => {
    try {
      setLoading(true);
      const response = await customerApi.getById(customerId);
      setCustomer(response.data);
    } catch (error) {
      message.error('Failed to fetch customer details');
      navigate(`${rolePrefix}/customers`);
    } finally {
      setLoading(false);
    }
  };

  const fetchVehicles = async (customerId: number) => {
    try {
      setVehiclesLoading(true);
      const response = await vehicleApi.getByCustomerId(customerId);
      setVehicles(response.data);
    } catch (error) {
      message.error('Failed to fetch vehicles');
    } finally {
      setVehiclesLoading(false);
    }
  };

  const handleBack = () => {
    navigate(`${rolePrefix}/customers`);
  };

  const getContactMethodIcon = (method: string) => {
    switch (method) {
      case 'phone':
        return <PhoneOutlined />;
      case 'email':
        return <MailOutlined />;
      default:
        return <PhoneOutlined />;
    }
  };

  const getCustomerDisplayName = (customer: Customer) => {
    return customer.company_name || `${customer.first_name} ${customer.last_name}`;
  };

  const handleAddVehicle = () => {
    setAddVehicleModalVisible(true);
  };

  const handleAddVehicleSuccess = () => {
    setAddVehicleModalVisible(false);
    fetchVehicles(parseInt(id!));
  };

  if (loading || !customer) {
    return (
      <div style={{ maxWidth: 1200, margin: '0 auto', width: '100%', overflowX: 'hidden' }}>
        <Card loading={true} />
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', width: '100%', overflowX: 'hidden' }}>
      <Space direction="vertical" size={24} style={{ width: '100%' }}>
        <Card>
          <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <div>
              <Button
                type="text"
                icon={<ArrowLeftOutlined />}
                onClick={handleBack}
                style={{ marginBottom: window.innerWidth >= 768 ? 16 : 12 }}
              >
                Back to Customers
              </Button>
              <Row
                justify="space-between"
                align="middle"
                gutter={[16, 16]}
                style={{ marginLeft: 0, marginRight: 0 }}
              >
                <Col xs={24} sm={16} md={18}>
                  <Space align="center" direction={window.innerWidth >= 640 ? 'horizontal' : 'vertical'} style={{ width: '100%' }}>
                    <Avatar
                      size={window.innerWidth >= 768 ? 64 : 48}
                      icon={<UserOutlined />}
                      style={{ backgroundColor: '#1890ff' }}
                    />
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <Title
                        level={window.innerWidth >= 768 ? 2 : 3}
                        style={{
                          margin: 0,
                          wordBreak: 'break-word',
                          overflowWrap: 'break-word'
                        }}
                      >
                        {getCustomerDisplayName(customer)}
                      </Title>
                      <Tag
                        icon={getContactMethodIcon(customer.preferred_contact_method || 'phone')}
                        color="blue"
                        style={{ marginTop: 8 }}
                      >
                        {customer.preferred_contact_method || 'phone'}
                      </Tag>
                    </div>
                  </Space>
                </Col>
                <Col xs={24} sm={8} md={6}>
                  <Button
                    type="primary"
                    icon={<EditOutlined />}
                    onClick={() => navigate(`${rolePrefix}/customers/${customer.id}/edit`)}
                    block={window.innerWidth < 640}
                    size={window.innerWidth >= 768 ? 'middle' : 'large'}
                  >
                    Edit Customer
                  </Button>
                </Col>
              </Row>
            </div>

            {(() => {
              const descCols = window.innerWidth >= 640 ? 2 : 1;
              const fullSpan = descCols;
              return (
                <Descriptions
                  column={descCols}
                  bordered
                  size={window.innerWidth >= 768 ? 'default' : 'small'}
                >
                  <Descriptions.Item label="Name" span={customer.company_name ? 1 : fullSpan}>
                    {customer.first_name} {customer.last_name}
                  </Descriptions.Item>
                  {customer.company_name && (
                    <Descriptions.Item label="Company">
                      {customer.company_name}
                    </Descriptions.Item>
                  )}
                  <Descriptions.Item label="Phone">
                    <Space size="small">
                      <PhoneOutlined />
                      <span style={{ wordBreak: 'break-word' }}>{customer.phone}</span>
                    </Space>
                  </Descriptions.Item>
                  <Descriptions.Item label="Email">
                    <Space size="small">
                      <MailOutlined />
                      <span style={{ wordBreak: 'break-word' }}>{customer.email}</span>
                    </Space>
                  </Descriptions.Item>
                  <Descriptions.Item label="Address" span={fullSpan}>
                    {customer.address}, {customer.city}, {customer.state} {customer.zip}
                  </Descriptions.Item>
                  {customer.notes && (
                    <Descriptions.Item label="Notes" span={fullSpan}>
                      {customer.notes}
                    </Descriptions.Item>
                  )}
                </Descriptions>
              );
            })()}
          </Space>
        </Card>

        <Card>
          <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <Row
              justify="space-between"
              align="middle"
              gutter={[16, 16]}
              style={{ marginLeft: 0, marginRight: 0 }}
            >
              <Col xs={24} sm={16} md={18}>
                <Title level={window.innerWidth >= 768 ? 3 : 4} style={{ margin: 0 }}>
                  <CarOutlined /> Vehicles ({vehicles.length})
                </Title>
              </Col>
              <Col xs={24} sm={8} md={6}>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  size={window.innerWidth >= 768 ? 'large' : 'middle'}
                  onClick={handleAddVehicle}
                  block={window.innerWidth < 640}
                  style={{ height: window.innerWidth >= 768 ? 48 : 40, fontSize: window.innerWidth >= 768 ? 16 : 14 }}
                >
                  Add Vehicle
                </Button>
              </Col>
            </Row>

            {vehicles.length === 0 ? (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="No vehicles registered for this customer"
                style={{ padding: window.innerWidth >= 768 ? '40px 0' : '20px 0' }}
              >
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  size={window.innerWidth >= 768 ? 'large' : 'middle'}
                  onClick={handleAddVehicle}
                  style={{ height: window.innerWidth >= 768 ? 48 : 40, fontSize: window.innerWidth >= 768 ? 16 : 14 }}
                >
                  Add First Vehicle
                </Button>
              </Empty>
            ) : (
              <Row
                gutter={[window.innerWidth >= 768 ? 16 : 12, window.innerWidth >= 768 ? 16 : 12]}
                style={{ marginLeft: 0, marginRight: 0 }}
              >
                {vehicles.map((vehicle) => (
                  <Col xs={24} sm={24} md={12} lg={12} xl={8} xxl={6} key={vehicle.id}>
                    <VehicleCard
                      vehicle={vehicle}
                      onUpdate={() => fetchVehicles(parseInt(id!))}
                    />
                  </Col>
                ))}
              </Row>
            )}
          </Space>
        </Card>

        {/* Rewards Section */}
        <Card>
          <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <Title level={window.innerWidth >= 768 ? 3 : 4} style={{ margin: 0 }}>
              <TrophyOutlined style={{ color: '#faad14' }} /> Rewards &amp; Loyalty
            </Title>
            <CustomerRewardsTab customerId={parseInt(id!)} />
          </Space>
        </Card>

        {/* Communications Section */}
        <Card>
          <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <Title level={window.innerWidth >= 768 ? 3 : 4} style={{ margin: 0 }}>
              <MessageOutlined style={{ color: '#1890ff' }} /> Communications History
            </Title>
            <CustomerCommunicationTab
              customerId={parseInt(id!)}
              customerPhone={customer.phone}
              customerEmail={customer.email}
            />
          </Space>
        </Card>

        <Modal
          title={`Add Vehicle for ${getCustomerDisplayName(customer)}`}
          open={addVehicleModalVisible}
          onCancel={() => setAddVehicleModalVisible(false)}
          footer={null}
          width="95%"
          style={{ maxWidth: 900, top: window.innerWidth >= 768 ? 20 : 10 }}
          styles={{ body: { maxHeight: 'calc(100vh - 120px)', overflowY: 'auto' } }}
          destroyOnHidden
        >
          <VehicleForm
            customerId={parseInt(id!)}
            onSuccess={handleAddVehicleSuccess}
            onCancel={() => setAddVehicleModalVisible(false)}
            modal
          />
        </Modal>
      </Space>
    </div>
  );
};

export default CustomerDetail;